import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';

interface MobileQuestionListProps {
  questions: string[];
  disabled?: boolean;
  onSelectQuestion: (question: string) => void;
}

const styles = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[3],
    flexShrink: 0,
  },
  heading: {
    margin: 0,
    fontSize: 16,
    fontWeight: tokens.font.weight.bold,
    color: tokens.text.primary,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[2],
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[3],
    width: '100%',
    textAlign: 'left',
    padding: `14px ${tokens.space[3]}`,
    background: tokens.surface.elevated,
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.md,
    boxShadow: '0 2px 10px rgba(17, 24, 39, 0.06)',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: tokens.font.weight.medium,
    color: tokens.text.secondary,
    fontFamily: 'inherit',
  },
  label: {
    flex: 1,
    minWidth: 0,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: tokens.icon.sparklesBg,
  },
} as const satisfies Record<string, CSSProperties>;

function SparklesIcon() {
  return (
    <span style={styles.iconWrap}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 50 50"
        aria-hidden="true"
      >
        <path
          fill={tokens.icon.accent}
          d="M22.462 11.035l2.88 7.097c1.204 2.968 3.558 5.322 6.526 6.526l7.097 2.88c1.312.533 1.312 2.391 0 2.923l-7.097 2.88c-2.968 1.204-5.322 3.558-6.526 6.526l-2.88 7.097c-.533 1.312-2.391 1.312-2.923 0l-2.88-7.097c-1.204-2.968-3.558-5.322-6.526-6.526l-7.097-2.88c-1.312-.533-1.312-2.391 0-2.923l7.097-2.88c2.968-1.204 5.322-3.558 6.526-6.526l2.88-7.097C20.071 9.723 21.929 9.723 22.462 11.035zM39.945 2.701l.842 2.428c.664 1.915 2.169 3.42 4.084 4.084l2.428.842c.896.311.896 1.578 0 1.889l-2.428.842c-1.915.664-3.42 2.169-4.084 4.084l-.842 2.428c-.311.896-1.578.896-1.889 0l-.842-2.428c-.664-1.915-2.169-3.42-4.084-4.084l-2.428-.842c-.896-.311-.896-1.578 0-1.889l2.428-.842c1.915-.664 3.42-2.169 4.084-4.084l.842-2.428C38.366 1.805 39.634 1.805 39.945 2.701z"
        />
      </svg>
    </span>
  );
}

export default function MobileQuestionList({
  questions,
  disabled,
  onSelectQuestion,
}: MobileQuestionListProps) {
  const { t } = useTranslation();
  const chips = questions.filter((item) => item.trim().length > 0);
  if (chips.length === 0) return null;

  return (
    <section style={styles.section} aria-label={t('popular.ariaLabel')}>
      <h2 style={styles.heading}>{t('mobile.popularHeading')}</h2>
      <ul style={styles.list}>
        {chips.map((label, index) => (
          <li key={`${index}-${label}`}>
            <button
              type="button"
              style={{
                ...styles.item,
                opacity: disabled ? 0.55 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              disabled={disabled}
              onClick={() => onSelectQuestion(label)}
            >
              <SparklesIcon />
              <span style={styles.label}>{label}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 6l6 6-6 6"
                  stroke={tokens.icon.accent}
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
